/**
 * PlayerHUD.tsx — WEB.4.UI.2
 *
 * 4 seat panels in a horizontal row. Shows per-seat tokens, energy, pollution,
 * and current tile position. Active seat panel has solar-amber border.
 * Fully store-driven — no props.
 */

import type React from "react";
import { computeTurnEndDeltas } from "../../store/aura-tick.js";
import { FACILITIES } from "../../store/facilities.js";
import { seatsInGame } from "../../store/game-end.js";
import { SEAT_COLORS, seatSymbolFor, useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
// R10 #7: HandSlotExpandBtn removed — it caused layout shift in main-screen PlayerHUD.
// Hand access in tile phase is via TileTopBar (new component, tile-phase only).
import { TILE_POSITIONS } from "../HexBoard/index.js";
import styles from "./PlayerHUD.module.css";

// Label per seat (1-indexed for display)
const SEAT_LABELS = ["P1", "P2", "P3", "P4"] as const;

/** Zero-pad a tile index to 2 digits. */
function padTile(idx: number): string {
  return idx.toString().padStart(2, "0");
}

/** Look up tile type name from TILE_POSITIONS by index. */
function getTileType(tileIdx: number): string {
  const pos = TILE_POSITIONS[tileIdx];
  return pos?.type ?? "Unknown";
}

/** R5 #10 */
interface OwnedFacilityEntry {
  tileIdx: number;
  facilityId: string;
  name: string;
  iconSrc: string;
}

interface SeatPanelProps {
  seat: SeatIndex;
  isActive: boolean;
  isBankrupt: boolean;
  jailTurns: number;
  bailPrice: number;
  escapeAttempts: number;
  /** codex R1 MED — disable escape after attempt this turn OR if not in turn-start idle state. */
  escapeDisabledByTurnGate: boolean;
  tileIdx: number;
  tokens: number;
  energy: number;
  pollution: number;
  /** R5 #10 */
  ownedFacilities: OwnedFacilityEntry[];
  onPayToLeaveJail: () => void;
  onAttemptEscapeJail: () => void;
  /** R11.2 — hide jail action buttons during board-walk animation. */
  hideJailActions: boolean;
  /**
   * Item B — projected next-turn energy delta. Null for bankrupt seats.
   * Positive = gain (green). Negative = loss (amber).
   */
  energyTick: number | null;
  /**
   * Item B — projected next-turn pollution delta. Null for bankrupt seats.
   * Always non-negative for pollution (amber).
   */
  pollutionTick: number | null;
}

function SeatPanel({
  seat,
  isActive,
  isBankrupt,
  jailTurns,
  bailPrice,
  escapeAttempts,
  escapeDisabledByTurnGate,
  tileIdx,
  tokens,
  energy,
  pollution,
  ownedFacilities,
  onPayToLeaveJail,
  onAttemptEscapeJail,
  hideJailActions,
  energyTick,
  pollutionTick,
}: SeatPanelProps): React.ReactElement {
  const seatColor = SEAT_COLORS[seat] ?? "#fff";
  // WEB.4.UI.10b: bankrupt seats render with desaturated panel + BANKRUPT chip.
  const panelClass = [
    styles.panel,
    isActive ? styles.panelActive : "",
    isBankrupt ? (styles.panelBankrupt ?? "") : "",
  ]
    .join(" ")
    .trim();

  // WEB.4.UI.PT-bonus: escape threshold depends on attempt count (25% first, 20% second)
  const escapeThreshold = escapeAttempts === 0 ? 25 : 20;

  return (
    <div
      className={panelClass}
      data-seat={seat}
      data-active={isActive ? "true" : undefined}
      data-bankrupt={isBankrupt ? "true" : undefined}
    >
      {/* Header strip in seat color */}
      <div className={styles.header} style={{ backgroundColor: seatColor, color: "#1a0a00" }}>
        {SEAT_LABELS[seat]}
        {/* R15 Builder-AW — colorblind seat symbol (aria-hidden; visual only) */}
        <span aria-hidden="true" style={{ marginLeft: 4, fontSize: "0.85em" }}>
          {seatSymbolFor(seat)}
        </span>
        {isBankrupt ? (
          <span className={styles.bankruptChip ?? ""} data-testid={`seat-bankrupt-${seat}`}>
            BANKRUPT
          </span>
        ) : null}
        {/* WEB.4.UI.PT-bonus jail chip — shows turns + dynamic bail price. */}
        {!isBankrupt && jailTurns > 0 ? (
          <span className={styles.jailChip} data-testid={`seat-jail-${seat}`}>
            <span aria-hidden="true">🔒</span> JAIL · {jailTurns}t · {bailPrice} tokens bail
          </span>
        ) : null}
      </div>

      <div className={styles.body}>
        {/* Current tile */}
        <div className={styles.tile}>
          {getTileType(tileIdx)} · T{padTile(tileIdx)}
        </div>

        {/* Resources */}
        <div className={styles.row}>
          <span aria-hidden="true">💰</span>
          <span aria-label={`${tokens} tokens`}>{tokens}</span>
        </div>
        <div
          className={styles.row}
          title={
            energyTick !== null
              ? `Energy next turn: ${energyTick >= 0 ? "+" : ""}${energyTick} from ${ownedFacilities.length} facilit${ownedFacilities.length === 1 ? "y" : "ies"}`
              : undefined
          }
        >
          <span aria-hidden="true">⚡</span>
          <span aria-label={`${energy} energy`}>{energy}</span>
          {energyTick !== null && (
            <span
              className={energyTick >= 0 ? styles.tickPositive : styles.tickNegative}
              data-testid={`seat-energy-tick-${seat}`}
              aria-label={`Energy tick: ${energyTick >= 0 ? "+" : ""}${energyTick}`}
            >
              ({energyTick >= 0 ? "+" : ""}
              {energyTick})
            </span>
          )}
        </div>
        <div
          className={styles.row}
          title={
            pollutionTick !== null
              ? `Pollution next turn: +${pollutionTick} from ${ownedFacilities.length} facilit${ownedFacilities.length === 1 ? "y" : "ies"}`
              : undefined
          }
        >
          <span aria-hidden="true">☠️</span>
          <span aria-label={`${pollution} pollution`}>{pollution}</span>
          {pollutionTick !== null && (
            <span
              className={styles.tickNegative}
              data-testid={`seat-pollution-tick-${seat}`}
              aria-label={`Pollution tick: +${pollutionTick}`}
            >
              (+{pollutionTick})
            </span>
          )}
        </div>

        {/* R6 #5 — Show owned-facility COUNT only (details on hex hover). */}
        <div className={styles.row}>
          <span aria-hidden="true">🏭</span>
          <span
            data-testid={`seat-facilities-count-${seat}`}
            aria-label={`${ownedFacilities.length} facilities`}
          >
            {ownedFacilities.length}
          </span>
        </div>

        {/* WEB.4.UI.PT-bonus: pay bail + try-escape buttons on active jailed seat.
            R11.2: also hidden while the seat token is still walking to its tile
            so the jail UI does not appear before the player visibly arrives. */}
        {isActive && !isBankrupt && jailTurns > 0 && !hideJailActions ? (
          <>
            <button
              type="button"
              className={styles.payJailBtn ?? ""}
              onClick={onPayToLeaveJail}
              disabled={tokens < bailPrice}
              title={`Pay ${bailPrice} tokens to leave jail immediately`}
              data-testid={`pay-jail-${seat}`}
            >
              Pay {bailPrice} tokens → leave
            </button>
            <button
              type="button"
              className={styles.escapeJailBtn ?? ""}
              onClick={onAttemptEscapeJail}
              disabled={escapeAttempts >= 2 || escapeDisabledByTurnGate}
              title={
                escapeDisabledByTurnGate
                  ? "Already attempted this turn — end turn to try again next round"
                  : `Try to escape jail (${escapeThreshold}% chance)`
              }
              data-testid={`escape-jail-${seat}`}
            >
              Try Escape ({escapeThreshold}%)
            </button>
          </>
        ) : null}
      </div>
      {/* R10 #7: HandSlotExpandBtn REMOVED. Main-screen seat panels stay fixed-height.
          Hand access during tile phase is via TileTopBar (mounted in SidescrollerScene). */}
    </div>
  );
}

export function PlayerHUD(): React.ReactElement {
  const currentSeat = useGameStore((s) => s.currentSeat);
  // R11.2 — gate jail buttons during board walk.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const tokens = useGameStore((s) => s.tokens);
  const energy = useGameStore((s) => s.energy);
  const pollution = useGameStore((s) => s.pollution);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const jailTurnsRemaining = useGameStore((s) => s.jailTurnsRemaining);
  const bailMultiplier = useGameStore((s) => s.bailMultiplier);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const jailEscapeAttempts = useGameStore((s) => s.jailEscapeAttempts);
  const jailEscapeUsedThisTurn = useGameStore((s) => s.jailEscapeUsedThisTurn);
  const turnPhase = useGameStore((s) => s.turnPhase);
  const payToLeaveJail = useGameStore((s) => s.payToLeaveJail);
  const attemptEscapeJail = useGameStore((s) => s.attemptEscapeJail);
  // R5 #10
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  // R9 perks — Politician bail × 1.5
  const playerPerks = useGameStore((s) => s.playerPerks);
  // Item B — per-turn tick preview
  const auraState = useGameStore((s) => s.auraState);
  const builtFacilityMeta = useGameStore((s) => s.builtFacilityMeta);

  // PRESENT seats only (index < numPlayers) — absent seats must not render a HUD
  // panel (phantom 100-token player) nor a tick-delta row in 2/3-player games.
  const seats: SeatIndex[] = seatsInGame(numPlayers);

  // R5 #10 — build per-seat owned-facility list
  function getOwnedFacilities(seatIdx: SeatIndex): OwnedFacilityEntry[] {
    return Object.entries(builtFacilities)
      .filter(([tileStr]) => ownedBy[Number(tileStr)] === seatIdx)
      .map(([tileStr, facilityId]) => {
        const def = FACILITIES.find((f) => f.id === facilityId);
        return {
          tileIdx: Number(tileStr),
          facilityId,
          name: def?.name ?? facilityId,
          iconSrc: def?.iconSrc ?? `/images/facilities/facility_${facilityId}.png`,
        };
      })
      .sort((a, b) => a.tileIdx - b.tileIdx);
  }

  // R3 Bug 5 spec: bail = max(1, round(meanAllPlayers × bailMultiplier[seat]))
  // Mirrors `computeBailPrice` in game-store.ts — all PRESENT seats including
  // bankrupt (excludes absent seats >= numPlayers, which keep INITIAL_TOKENS=100
  // and would inflate the mean in 2/3-player games), Math.round (NOT ceil). Codex
  // precommit flagged HUD/store divergence: HUD previously used activeSeats + ceil.
  const tokenMean = seats.reduce<number>((sum, i) => sum + (tokens[i] ?? 0), 0) / seats.length;

  // Item B — compute projected next-turn deltas per seat (read-only, no mutation).
  // Bankrupt seats get null (no tick shown). Uses computeTurnEndDeltas (same
  // function endTurn calls) so the preview is always in sync with reality.
  // Codex Phase 2a MED 2 fix: hide ticks while the seat token is still walking
  // — the projected delta is meaningless until arrival; matches R11.2 gate.
  const tickDeltas: Array<{ energy: number; pollution: number } | null> = seats.map((s) => {
    if (lostSeats.has(s)) return null;
    if (boardAnimationActive) return null;
    return computeTurnEndDeltas(
      builtFacilities as Record<number, string | undefined>,
      ownedBy as Record<number, number | undefined>,
      auraState,
      s,
      builtFacilityMeta,
      playerPerks[s],
    );
  });

  return (
    <div className={styles.hud} data-testid="player-hud">
      {seats.map((seat) => {
        const baseBailPrice = Math.max(1, Math.round(tokenMean * (bailMultiplier[seat] ?? 0.25)));
        // R9 perk: Politician bail × 1.5
        const bailPrice =
          playerPerks[seat] === "politician"
            ? Math.max(1, Math.ceil(baseBailPrice * 1.5))
            : baseBailPrice;
        const tick = tickDeltas[seat] ?? null;
        return (
          <SeatPanel
            key={seat}
            seat={seat}
            isActive={seat === currentSeat}
            isBankrupt={lostSeats.has(seat)}
            jailTurns={jailTurnsRemaining[seat] ?? 0}
            bailPrice={bailPrice}
            escapeAttempts={jailEscapeAttempts[seat] ?? 0}
            escapeDisabledByTurnGate={
              (jailEscapeUsedThisTurn[seat] ?? false) || turnPhase !== "idle"
            }
            tileIdx={playerTiles[seat] ?? 0}
            tokens={tokens[seat] ?? 0}
            energy={energy[seat] ?? 0}
            pollution={pollution[seat] ?? 0}
            ownedFacilities={getOwnedFacilities(seat)}
            onPayToLeaveJail={payToLeaveJail}
            onAttemptEscapeJail={attemptEscapeJail}
            hideJailActions={boardAnimationActive}
            energyTick={tick !== null ? tick.energy : null}
            pollutionTick={tick !== null ? tick.pollution : null}
          />
        );
      })}
    </div>
  );
}
